import { NgStyle } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { untilDestroyed } from '@jump-tech-frontend/angular-common';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { JUI_INPUT_IMPORTS } from '@jump-tech-frontend/core-components-angular';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { QuestionHintComponent, QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { ImageAssetFactory } from '../../domain/card';
import { ImageAsset, ImageAssetType } from '../../domain/types';
import { ImageAssetComponent } from '../image-asset.component';
import { BaseQuestionComponent } from '../question.component';
import { ScrollService } from '../scroll.service';
import { DateQuestionService } from './date-question/date-question.service';
import { buildMessages } from './question.utils';

@Component({
  selector: `crds-question-textbox`,
  template: `
    @if (show && form) {
      <div [formGroup]="form">
        @if (this.useJuiControls) { @if (isDateWithIso) {
          <div [formGroup]="displayForm">
            <jui-input
              #textInput
              [size]="controlSize"
              type="date"
              [formControlName]="question.key + '__display'"
              [label]="question.label"
              [dataqastring]="question.key + 'Input'"
              [required]="question.required ?? false"
              [readonly]="question.readOnly"
              [tooltip]="buildTooltip()"
              (juiChange)="onChange()"
              [validation]="'onFocus'"
            ></jui-input>
          </div>
          <div [formGroup]="form">
            <input [formControlName]="question.key" type="hidden" />
          </div>
        } @else {
          <jui-input
            #textInput
            [size]="controlSize"
            [type]="question['type'] || 'text'"
            [formControlName]="question.key"
            [label]="question.label"
            [dataqastring]="question.key + 'Input'"
            [required]="question.required ?? false"
            [readonly]="question.readOnly"
            [minlength]="getMinAttribute(question) ?? null"
            [disable]="form.get(question.key).disabled"
            [errors]="form.get(question.key)?.errors"
            [messages]="buildMessages(this.form, this.question, this.i18ns)"
            [tooltip]="buildTooltip()"
            (juiChange)="onChange()"
            [validation]="'onFocus'"
          ></jui-input>
          } @if(question.permanentHint) {
          <question-hint [question]="question"></question-hint>
        }
        <!-- QUESTION IMAGE -->
        <div class="image-wrapper">
          <crds-image-asset [images]="images" [key]="question.key"></crds-image-asset>
        </div>
      } @else {
        <!-- QUESTION LABEL -->
        @if (question.label) {
          <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
            <question-label-content [question]="question" [isInvalid]="isInvalid" [i18ns]="i18ns"></question-label-content>
          </label>
        }
        <!-- QUESTION INPUT -->
        @if (isDateWithIso) {
          <div [formGroup]="displayForm">
            <input
              [formControlName]="question.key + '__display'"
              [attr.data-qa]="question.key + 'Input'"
              class="form-control mb-2"
              type="date"
              [readOnly]="question.readOnly"
              #textInput
              />
          </div>
          <div [formGroup]="form">
            <input [id]="question.key" [formControlName]="question.key" type="hidden" />
          </div>
        } @else {
          <input
            [formControlName]="question.key"
            [id]="question.key"
            [type]="question['type'] || 'text'"
            [readOnly]="question.readOnly"
            (change)="onChange()"
            [ngStyle]="{ 'text-transform': this.question.forceUpper && 'uppercase' }"
            class="form-control mb-2"
            [attr.data-qa]="question.key + 'Input'"
            #textInput
            [attr.min]="getMinAttribute(question)"
            [placeholder]="question.placeholder ?? ''"
            />
        }
        <!-- QUESTION HINT -->
        <question-hint [question]="question"></question-hint>
        <!-- QUESTION IMAGE -->
        <div class="image-wrapper">
          <crds-image-asset [images]="images" [key]="question.key"></crds-image-asset>
        </div>
      }
    </div>
    }
    `,
  styles: [
    `
      [readonly] {
        color: -internal-light-dark(rgb(84, 84, 84), rgb(170, 170, 170));
        cursor: default;
        background-color: -internal-light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3));
        border-color: rgba(118, 118, 118, 0.3);
        pointer-events: none;
      }
    `
  ],
  imports: [
    ReactiveFormsModule,
    QuestionLabelContentComponent,
    NgStyle,
    QuestionHintComponent,
    ImageAssetComponent,
    ...JUI_INPUT_IMPORTS
]
})
export class TextboxQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<string>;
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls?: boolean = false;
  @Input() deviceContext?: string = 'desktop';

  @ViewChild('textInput') input: ElementRef;
  type: string;
  images: ImageAsset[];
  imageAssetType = ImageAssetType;
  isDateWithIso = false;
  displayForm: UntypedFormGroup;
  controlSize = 'sm';
  minValue = 0;

  private untilDestroyed = untilDestroyed();

  constructor(
    private imageAssetFactory: ImageAssetFactory,
    private scrollService: ScrollService,
    private dateQuestionService: DateQuestionService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    this.controlSize = this.getControlSize();

    // Check for ISO date handling
    const currentValue = this.form.get(this.question.key)?.value;
    const isDateType = this.question.type === 'date';
    this.isDateWithIso = isDateType && this.dateQuestionService.valueIsDateIso(currentValue);

    if (this.isDateWithIso) {
      this.buildDateDisplayForm();
    }

    if (this.question.disabled === true && !this.show) {
      setTimeout(() => this.form?.get(this.question.key)?.disable());
    }
    this.images = this.imageAssetFactory.create(this.question.image, this.form);
    this.scrollService.scrollObservable.pipe(this.untilDestroyed()).subscribe(documentId => {
      if (documentId === this.question.key) {
        this.input.nativeElement.scrollIntoView();
      }
    });

    if (this.question.min) {
      this.minValue = parseInt(this.question.min);
    }
  }

  /** Though we can force to uppercase using CSS, that doesn't change the actual value
   *  so when we leave the field, we also need to upperCase the value too
   */
  onChange() {
    // todo - only used in car details - not sure if this will be required functionality anymore R.B
    if (this.question.forceUpper) {
      const field = this.form.get(this.question.key);
      if (field?.value) {
        field.setValue(field.value.toUpperCase());
      }
    }
  }

  override get isInvalid() {
    // todo this is pointless as it does nothing
    if (this.checkForNegativeNumber()) {
      this.form.setErrors({ negativeNumber: true });
    }
    return (this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid) ?? false;
  }

  override get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }
  getMinAttribute(question: QuestionBase<string>) {
    if (question.type === 'number' && question.min !== undefined) {
      return question.min;
    }
    return null;
  }
  checkForNegativeNumber() {
    if (this.question.type === 'number' && this.question.min !== undefined) {
      return this.form.get(this.question.key)?.value < 0 ? true : false;
    }
    return false;
  }

  buildTooltip(): string {
    return this.question.hint && !this.question.permanentHint ? this.question.hint : '';
  }

  getControlSize() {
    return this.deviceContext === 'mobile' ? 'lg' : 'sm';
  }

  private buildDateDisplayForm(): void {
    const isoValue = this.form.get(this.question.key)?.value;
    this.displayForm = this.dateQuestionService.buildDateDisplayForm(this.question.key, isoValue, this.form);
  }

  protected readonly buildMessages = buildMessages;
}
